/**
 * The account's custom tools, one of them, the slug check and the writes. A write also refreshes the
 * tool definitions, where each custom tool is listed as `custom:<slug>`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import { customToolService } from '@/services/customTool.service';
import type {
	CustomToolPayload,
	CustomToolSuggestionRequest,
	CustomToolTestRequest,
	CustomToolUpdate,
	SlugTakenReason
} from '@/types/custom-tool.types';

const CUSTOM_TOOLS_KEY = 'custom-tools';
const SLUG_CHECK_DELAY_MS = 400;

const refreshAfterWrite = () => Promise.all([invalidateQueries([CUSTOM_TOOLS_KEY]), invalidateQueries(['tool-definitions'])]);

const customToolsQueryAtom = atomWithQuery(() => ({
	queryKey: [CUSTOM_TOOLS_KEY],
	queryFn: () => customToolService.list()
}));

export function useCustomTools() {
	const [query] = useAtom(customToolsQueryAtom);
	return query;
}

const customToolQueryAtom = (id: string) =>
	atomWithQuery(() => ({
		queryKey: [CUSTOM_TOOLS_KEY, 'detail', id],
		queryFn: () => customToolService.get(id),
		enabled: !!id
	}));

export function useCustomTool(id: string) {
	const atom = useMemo(() => customToolQueryAtom(id), [id]);
	const [query] = useAtom(atom);
	return query;
}

const slugCheckQueryAtom = (slug: string) =>
	atomWithQuery(() => ({
		queryKey: [CUSTOM_TOOLS_KEY, 'slug-check', slug],
		queryFn: () => customToolService.checkSlug(slug),
		enabled: !!slug,
		retry: false
	}));

export type SlugStatus = 'idle' | 'checking' | 'available' | 'failed' | 'taken' | SlugTakenReason;

/** Asks once typing pauses; `idle` while there is nothing to check. */
export function useSlugStatus(slug: string, enabled: boolean): SlugStatus {
	const wanted = enabled ? slug : '';
	const [settled, setSettled] = useState(wanted);
	useEffect(() => {
		const timer = setTimeout(() => setSettled(wanted), SLUG_CHECK_DELAY_MS);
		return () => clearTimeout(timer);
	}, [wanted]);
	const atom = useMemo(() => slugCheckQueryAtom(settled), [settled]);
	const [{ data, isError, isFetching }] = useAtom(atom);

	if (!wanted) return 'idle';
	if (settled !== wanted || isFetching) return 'checking';
	if (isError) return 'failed';
	if (!data) return 'checking';
	return data.available ? 'available' : (data.reason ?? 'taken');
}

// A 409, 400 or 404 is the answer, not a blip worth sending again.
const createCustomToolMutationAtom = atomWithMutation((get) => ({
	mutationKey: [CUSTOM_TOOLS_KEY, 'create'],
	mutationFn: (payload: CustomToolPayload) => customToolService.create(payload, buScopeParams(get)),
	retry: false,
	onSuccess: refreshAfterWrite
}));

export function useCreateCustomTool() {
	const [mutation] = useAtom(createCustomToolMutationAtom);
	return mutation;
}

const updateCustomToolMutationAtom = atomWithMutation((get) => ({
	mutationKey: [CUSTOM_TOOLS_KEY, 'update'],
	mutationFn: ({ id, update }: { id: string; update: CustomToolUpdate }) =>
		customToolService.update(id, update, buScopeParams(get)),
	retry: false,
	onSuccess: refreshAfterWrite
}));

export function useUpdateCustomTool() {
	const [mutation] = useAtom(updateCustomToolMutationAtom);
	return mutation;
}

const deleteCustomToolMutationAtom = atomWithMutation(() => ({
	mutationKey: [CUSTOM_TOOLS_KEY, 'delete'],
	mutationFn: (id: string) => customToolService.remove(id),
	retry: false,
	onSuccess: refreshAfterWrite
}));

export function useDeleteCustomTool() {
	const [mutation] = useAtom(deleteCustomToolMutationAtom);
	return mutation;
}

const suggestCustomToolMutationAtom = atomWithMutation((get) => ({
	mutationKey: [CUSTOM_TOOLS_KEY, 'suggest'],
	mutationFn: (request: CustomToolSuggestionRequest) => customToolService.suggest(request, buScopeParams(get)),
	retry: false
}));

export function useSuggestCustomTool() {
	const [mutation] = useAtom(suggestCustomToolMutationAtom);
	return mutation;
}

// A test run calls the live tools; the app-wide retry would run them twice.
const testCustomToolMutationAtom = atomWithMutation((get) => ({
	mutationKey: [CUSTOM_TOOLS_KEY, 'test'],
	mutationFn: (request: CustomToolTestRequest) => customToolService.test(request, buScopeParams(get)),
	retry: false
}));

export function useTestCustomTool() {
	const [mutation] = useAtom(testCustomToolMutationAtom);
	return mutation;
}
